import {
  useQueries,
  type QueryFunctionContext,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { CommonAggregation } from '../../types/domain';
import {
  dataApiClient,
  type AttributeAggregationType,
} from '../../infrastructure/dataApi/client';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { useMetaData } from './useMetaData';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';
import { useSchemaComparison } from '../SimViewer/useSchemaComparison';

type UseAttributeAggregationComparisonsData = {
  isLoading: boolean;
  aggregationsMap: Record<string, Map<string, number>>;
};

const EMPTY_DATA = new Map<string, never>();

const useAttributeAggregationComparisonsData = (
  type: AttributeAggregationType
): UseAttributeAggregationComparisonsData => {
  const { currentSimDataId } = useCurrentSimDataId();
  const { comparisons } = useSchemaComparison();
  const { allDataMetas } = useMetaData();
  const { apiFilter } = useApiDataFilter();

  // 棒グラフにはフィルターで除外せずに全要素を表示したいため条件から除外する
  const filter = { ...apiFilter, [type]: undefined };
  const simDataIds = currentSimDataId ? [currentSimDataId, ...comparisons] : [];

  const results = useQueries({
    queries: simDataIds.map((simDataId: string) => {
      return {
        queryKey: ['attributeAggregation', simDataId, filter, type],
        queryFn: async ({
          signal,
        }: QueryFunctionContext): Promise<CommonAggregation[]> => {
          return dataApiClient.getAttrAggr(simDataId, filter, type, signal);
        },
        meta: { dataType: 'others' },
      };
    }),
  });

  return {
    isLoading: results.some((result: UseQueryResult): boolean => {
      return result.isFetching;
    }),
    aggregationsMap: Object.fromEntries(
      simDataIds.map(
        (simDataId: string, index: number): [string, Map<string, number>] => {
          const viewMagnification =
            1 / Number(allDataMetas.get(simDataId)?.reduction_factor ?? 1);
          return [
            simDataId,
            results[index].data
              ? new Map(
                  results[index].data.map(
                    (item: CommonAggregation): [string, number] => {
                      return [item.label, item.value * viewMagnification];
                    }
                  )
                )
              : EMPTY_DATA,
          ];
        }
      )
    ),
  };
};

export { useAttributeAggregationComparisonsData };
